import { useState } from "react";
import { XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { rejectEndorsement } from "@/Api/endorsements";
import { Endorsement } from "@/types/endorsement";

type RejectModalProps = {
  endorsement: Endorsement | null;
  onClose: () => void;
  onSuccess: () => void;
};

export const EndorsementRejectModal = ({ endorsement, onClose, onSuccess }: RejectModalProps) => {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setNotes("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!endorsement || !notes.trim()) return;

    setSubmitting(true);
    try {
      const { ok } = await rejectEndorsement(endorsement.uuid, notes.trim());
      if (!ok) return;

      toast.success("Endossamento recusado.");
      setNotes("");
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!endorsement} onOpenChange={(open) => !open && handleClose()}>
      <AppDialogContent
        icon={XCircle}
        title="Recusar Endossamento"
        description={endorsement?.client_name}
      >
        <AppDialogBody>
          <div className="space-y-2">
            <Label htmlFor="endorsement_reject_notes">Motivo da recusa *</Label>
            <Textarea
              id="endorsement_reject_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Explique ao vendedor o motivo da recusa..."
              className="min-h-[100px]"
            />
            <p className="text-xs text-gray-500">
              Após a recusa, o vendedor poderá solicitar o endossamento novamente.
            </p>
          </div>
        </AppDialogBody>
        <AppDialogFooter>
          <Button type="button" className={appDialogSecondaryButton} onClick={handleClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            className={appDialogPrimaryButton}
            onClick={handleSubmit}
            disabled={submitting || !notes.trim()}
          >
            {submitting ? "Recusando..." : "Recusar"}
          </Button>
        </AppDialogFooter>
      </AppDialogContent>
    </Dialog>
  );
};
